// Diálogo de confirmación con el theme del manager — reemplaza window.confirm
// (que sale con el chrome nativo del SO, fuera de diseño). Portal a body.
//
// Dos formas de usarlo:
//   1. <ConfirmDialog open={...} onConfirm={...} onCancel={...} />  (controlado)
//   2. const { confirm, dialog } = useConfirmDialog();               (promesa)
//      if (!(await confirm({ title: '¿Borrar?' }))) return;  ... {dialog} en el JSX
// La 2ª existe porque migrar un `window.confirm` suelto con el componente
// controlado obliga a inventar estado y a partir el handler en dos.
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle } from 'lucide-react';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  icon?: ReactNode;
  keyboard?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
}

export default function ConfirmDialog({
  open, title, message, confirmLabel = 'Confirmar', cancelLabel = 'Cancelar',
  icon, onConfirm, onCancel, busy = false, keyboard = false,
}: ConfirmDialogProps) {
  // Atajos opcionales (keyboard): Enter = confirmar, Esc = cancelar. Pensado para cajas que capturan sin soltar el teclado
  // (Repartos «¿los datos son correctos?»); apagado por defecto para no confirmar borrados por un Enter perdido.
  useEffect(() => {
    if (!open || !keyboard) return undefined;
    const h = (e: KeyboardEvent) => {
      if (busy) return;
      if (e.key === 'Enter') { e.preventDefault(); onConfirm(); }
      if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, keyboard, busy, onConfirm, onCancel]);
  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[1500] overflow-y-auto">
      <div className="modal-overlay fixed inset-0 bg-black bg-opacity-40" onClick={busy ? undefined : onCancel} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="modal-panel relative bg-surface rounded-xl shadow-xl w-full max-w-sm">
          <div className="flex items-center px-5 py-4 border-b border-line">
            {icon ?? <AlertTriangle className="h-5 w-5 text-amber-500 mr-2.5 flex-shrink-0" />}
            <h3 className="text-base font-semibold text-content">{title}</h3>
          </div>
          <div className="px-5 py-4 text-sm text-content-2">{message}</div>
          <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-line bg-surface-2 rounded-b-xl">
            <button
              onClick={onCancel}
              disabled={busy}
              className="px-4 py-2 text-sm font-medium text-content-2 bg-surface border border-line rounded-lg hover:bg-surface-2 transition-colors disabled:opacity-50"
            >
              {cancelLabel}
            </button>
            <button
              onClick={onConfirm}
              disabled={busy}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {busy ? 'Un momento…' : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ============================================================================
// Envoltorio de promesa sobre el mismo diálogo de arriba.
// ============================================================================
export interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  icon?: ReactNode;
  keyboard?: boolean;
}

export function useConfirmDialog() {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback((o: ConfirmOptions) => {
    setOpts(o);
    return new Promise<boolean>((resolve) => { resolver.current = resolve; });
  }, []);

  const close = (v: boolean) => {
    resolver.current?.(v);
    resolver.current = null;
    setOpts(null);
  };

  const dialog = (
    <ConfirmDialog
      open={!!opts}
      title={opts?.title || ''}
      message={opts?.message ?? ''}
      confirmLabel={opts?.confirmLabel}
      cancelLabel={opts?.cancelLabel}
      icon={opts?.icon}
      keyboard={opts?.keyboard}
      onConfirm={() => close(true)}
      onCancel={() => close(false)}
    />
  );

  return { confirm, dialog };
}

// ============================================================================
// PromptDialog — para el `window.prompt`, que es la ventana gris más fea:
// caja de texto cruda del SO. Devuelve el texto o null si se cancela.
// ============================================================================
export interface PromptOptions {
  title: string;
  message?: string;
  placeholder?: string;
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** true = no deja confirmar con el campo vacío */
  required?: boolean;
}

export function usePromptDialog() {
  const [opts, setOpts] = useState<PromptOptions | null>(null);
  const [value, setValue] = useState('');
  const resolver = useRef<((v: string | null) => void) | null>(null);

  const prompt = useCallback((o: PromptOptions) => {
    setValue(o.defaultValue || '');
    setOpts(o);
    return new Promise<string | null>((resolve) => { resolver.current = resolve; });
  }, []);

  const close = (v: string | null) => {
    resolver.current?.(v);
    resolver.current = null;
    setOpts(null);
  };

  const puedeConfirmar = !opts?.required || value.trim().length > 0;

  const dialog = opts ? createPortal(
    <div className="fixed inset-0 z-[1500] overflow-y-auto">
      <div className="modal-overlay fixed inset-0 bg-black bg-opacity-40" onClick={() => close(null)} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="modal-panel relative bg-surface rounded-xl shadow-xl w-full max-w-sm">
          <div className="px-5 py-4 border-b border-line">
            <h3 className="text-base font-semibold text-content">{opts.title}</h3>
            {opts.message && <p className="mt-1 text-sm text-content-2">{opts.message}</p>}
          </div>
          <div className="px-5 py-4">
            <input
              autoFocus
              type="text"
              value={value}
              placeholder={opts.placeholder}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && puedeConfirmar) close(value);
                if (e.key === 'Escape') close(null);
              }}
              className="w-full px-3 py-2 text-sm bg-surface-2 border border-line rounded-lg text-content outline-none focus:border-green-600"
            />
          </div>
          <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-line bg-surface-2 rounded-b-xl">
            <button
              onClick={() => close(null)}
              className="px-4 py-2 text-sm font-medium text-content-2 bg-surface border border-line rounded-lg hover:bg-surface-2 transition-colors"
            >
              {opts.cancelLabel || 'Cancelar'}
            </button>
            <button
              onClick={() => close(value)}
              disabled={!puedeConfirmar}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700 transition-colors disabled:opacity-40"
            >
              {opts.confirmLabel || 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return { prompt, dialog };
}
